/**
 * TanStack Query hooks wrapping the comms API layer (`./api`, which in turn
 * calls the generated SDK), following the style of `src/lib/api/hooks.ts`
 * (one hook per endpoint; mutations invalidate the query keys they make
 * stale).
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  approveNudge,
  demoNextEvent,
  demoReset,
  getCustomerPreferences,
  getDemoQueue,
  getOrderingCustomer,
  getShareAccount,
  getShareDraft,
  getShareQuarters,
  getShareSummary,
  listOrderEmails,
  listOrderingCustomers,
  listShareNudges,
  skipNudge,
  updateCustomerPreferences,
} from "./api";
import type { NotificationPreferences, PageParams, ShareRange } from "./types";

// ── Ordering events ──────────────────────────────────────────────────────

export function useOrderingCustomers(params: PageParams = {}) {
  return useQuery({
    queryKey: ["comms", "ordering", "customers", params],
    queryFn: () => listOrderingCustomers(params),
    // Keep the previous page on screen while the next page/search loads,
    // rather than flashing the table blank.
    placeholderData: keepPreviousData,
  });
}

export function useOrderingCustomer(customerId: string | null) {
  return useQuery({
    queryKey: ["comms", "ordering", "customers", customerId],
    queryFn: () => getOrderingCustomer(customerId!),
    enabled: !!customerId,
  });
}

export function useOrderEmails(orderId: string | null) {
  return useQuery({
    queryKey: ["comms", "ordering", "orders", orderId, "emails"],
    queryFn: () => listOrderEmails(orderId!),
    enabled: !!orderId,
  });
}

export function usePreferences(customerId: string | null) {
  return useQuery({
    queryKey: ["comms", "ordering", "customers", customerId, "preferences"],
    queryFn: () => getCustomerPreferences(customerId!),
    enabled: !!customerId,
  });
}

export function useUpdatePreferences(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (preferences: NotificationPreferences) =>
      updateCustomerPreferences(customerId, preferences),
    onSuccess: (data) => {
      // Seed the cache first so the toggle never falls back to the stale value
      // during the refetch window.
      queryClient.setQueryData(["comms", "ordering", "customers", customerId, "preferences"], data);
      // Prefs drive the opted-in badge and future suppressions — refresh the
      // whole ordering branch (list + detail + prefs).
      void queryClient.invalidateQueries({ queryKey: ["comms", "ordering"] });
    },
  });
}

// ── Order share ──────────────────────────────────────────────────────────

export function useShareQuarters() {
  return useQuery({
    queryKey: ["comms", "share", "quarters"],
    queryFn: () => getShareQuarters(),
  });
}

export function useShareSummary(quarter: string | null | undefined, params: PageParams = {}) {
  return useQuery({
    queryKey: ["comms", "share", "summary", quarter, params],
    queryFn: () => getShareSummary(quarter!, params),
    enabled: !!quarter,
    // Keep the previous page on screen while the next page/search loads,
    // rather than flashing the table blank.
    placeholderData: keepPreviousData,
  });
}

export function useShareAccount(accountId: string | null, range: ShareRange = "3") {
  return useQuery({
    queryKey: ["comms", "share", "accounts", accountId, "detail", range],
    queryFn: () => getShareAccount(accountId!, range),
    enabled: !!accountId,
  });
}

export function useShareNudges(accountId: string | null) {
  return useQuery({
    queryKey: ["comms", "share", "accounts", accountId, "nudges"],
    queryFn: () => listShareNudges(accountId!),
    enabled: !!accountId,
  });
}

export function useDraft(accountId: string | null) {
  return useQuery({
    queryKey: ["comms", "share", "accounts", accountId, "draft"],
    queryFn: () => getShareDraft(accountId!),
    enabled: !!accountId,
  });
}

export function useApproveNudge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { accountId: string }) => approveNudge(vars.accountId),
    onSuccess: () => {
      // Approval flips the summary row, account markers and draft state.
      void queryClient.invalidateQueries({ queryKey: ["comms", "share"] });
    },
  });
}

export function useSkipNudge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { accountId: string }) => skipNudge(vars.accountId),
    onSuccess: () => {
      // Skipping resolves the same three views an approval does.
      void queryClient.invalidateQueries({ queryKey: ["comms", "share"] });
    },
  });
}

// ── Demo controls ────────────────────────────────────────────────────────

export function useDemoQueue() {
  return useQuery({
    queryKey: ["admin", "demo", "queue"],
    queryFn: () => getDemoQueue(),
  });
}

export function useTriggerNextEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => demoNextEvent(),
    onSuccess: () => {
      // A processed event changes order progress, email logs and the queue.
      void queryClient.invalidateQueries({ queryKey: ["comms", "ordering"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "demo"] });
    },
  });
}

export function useDemoReset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => demoReset(),
    onSuccess: () => {
      // Reset restores the whole world — drop everything cached.
      void queryClient.invalidateQueries();
    },
  });
}
